import React from 'react';
import { ACTIVITY_TYPOLOGY, THEME_TYPOLOGY, normalizeMainTheme } from '../data/taxonomies.js';
import { ACTIVITY_DETAILS, THEME_DETAILS } from '../data/typology_details.js';

// ============================================================
// Typology page — explorable cards + lens graphics
// ============================================================

const { useState: tpUseState, useRef: tpUseRef, useEffect: tpUseEffect } = React;

export function TypologyPage({ onNavigate }) {
  const [openKey, setOpenKey] = tpUseState(null); // e.g. "act:Activism & Advocacy"
  const [tab, setTab] = tpUseState('activities'); // activities | themes | lenses

  return (
    <div className="static-page typology-page">
      {/* Intro */}
      <section className="about-intro">
        <div className="about-intro-inner">
          <div className="static-eyebrow">Categorisation</div>
          <h1 className="static-title">How we categorise<br/>the ecosystem.</h1>
          <p className="static-lede">
            Every organisation in the Index is described along a handful of
            dimensions, so you can filter the field in the way that matters to
            you. Two <strong>typologies</strong> (Activity and Themes) sit at the
            center; other <strong>lenses</strong> help quality each organisation
            in terms of stakeholders, geography, policy, power, and implementation.
          </p>
          <p className="static-lede" style={{fontSize: 14}}>
            Read more in the report <a href="https://www.metabolic.nl/publications/fertile-ground/" target="_blank" rel="noopener noreferrer" className="inline-link">Fertile Ground</a>, Section 4: Navigating the Field.
          </p>
        </div>
      </section>

      {/* Tab bar */}
      <div className="typ-tabs-wrap">
        <p className="typ-tabs-caption">Tap any category below to unpack its sub-categories, definitions and examples.</p>
        <div className="typ-tabs">
          <button className={tab === 'activities' ? 'on' : ''} onClick={() => { setTab('activities'); setOpenKey(null); }}>
            <span className="typ-tab-num" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/></svg>
            </span>
            <div>
              <div className="typ-tab-t">Activities</div>
              <div className="typ-tab-s">What organisations <em>do</em></div>
            </div>
          </button>
          <button className={tab === 'themes' ? 'on' : ''} onClick={() => { setTab('themes'); setOpenKey(null); }}>
            <span className="typ-tab-num" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0l-7.4-7.4A2 2 0 012.6 11.8V4.6a2 2 0 012-2h7.2a2 2 0 011.4.6l7.4 7.4a2 2 0 010 2.8z"/><circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none"/></svg>
            </span>
            <div>
              <div className="typ-tab-t">Themes</div>
              <div className="typ-tab-s">What organisations work <em>on</em></div>
            </div>
          </button>
          <button className={tab === 'lenses' ? 'on' : ''} onClick={() => { setTab('lenses'); setOpenKey(null); }}>
            <span className="typ-tab-num" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M20 20l-4.5-4.5"/></svg>
            </span>
            <div>
              <div className="typ-tab-t">Lenses</div>
              <div className="typ-tab-s">Stakeholders, policy, power, implementation, geography </div>
            </div>
          </button>
        </div>
      </div>

      {tab === 'activities' && (
        <TypologyCatPanel
          kind="activity"
          eyebrow="Activity-based typology"
          title="What organisations do."
          blurb="This typology categorises organisations by what they do, capturing modes of action and strategies for change."
          graphic={(window.__resources && window.__resources.typologyActivity) || "/assets/typology-activity.png"}
          graphicAlt="Activity-based typology wheel"
          categories={ACTIVITY_TYPOLOGY}
          details={ACTIVITY_DETAILS}
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
      )}

      {tab === 'themes' && (
        <TypologyCatPanel
          kind="theme"
          eyebrow="Thematic typology"
          title="What they work on."
          blurb="This typology categorises organisations by the issues they work on, capturing topical and thematic areas."
          graphic={(window.__resources && window.__resources.typologyTheme) || "/assets/typology-theme.png"}
          graphicAlt="Thematic typology wheel"
          categories={THEME_TYPOLOGY}
          details={THEME_DETAILS}
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
      )}

      {tab === 'lenses' && <LensesPanel />}

      {/* CTA */}
      <section className="static-section">
        <div className="static-col-full" style={{textAlign: 'center'}}>
          <h2 className="static-h2">Browse organisations with these filters.</h2>
          <p style={{maxWidth: 560, margin: '0 auto 24px'}}>
            Every filter in the directory maps to a category above.
          </p>
          <a className="btn-primary" href="#/?view=wheels" onClick={(e) => { e.preventDefault(); onNavigate('directory', {view: 'wheels'}); }}>Open the directory →</a>
        </div>
      </section>
    </div>
  );
}

export function TypologyCatPanel({ kind, eyebrow, title, blurb, graphic, graphicAlt, categories, details, openKey, setOpenKey }) {
  const detailsMap = React.useMemo(() => {
    const m = {};
    (details || []).forEach(d => {
      m[d.name] = d;
      // Also key themes under their normalized name so taxonomy/detail name
      // variants (e.g. "Place-Based Resilience" vs "Place-Based Transition & Resilience") match.
      if (kind === 'theme' && normalizeMainTheme) {
        m[normalizeMainTheme(d.name)] = d;
      }
    });
    return m;
  }, [details, kind]);

  return (
    <section className="typ-panel">
      <div className="typ-panel-inner">
        <div className="typ-panel-head">
          <div>
            <div className="static-eyebrow">{eyebrow}</div>
            <h2 className="static-h2 static-h2-lg typ-panel-title">{title}</h2>
            <p className="typ-panel-blurb">{blurb}</p>
          </div>
          <figure className="typ-panel-graphic">
            <WheelExplorer src={graphic} alt={graphicAlt}/>
          </figure>
        </div>

        <div className="typ-cards">
          {categories.map(cat => {
            const key = `${kind}:${cat.name}`;
            const open = openKey === key;
            const d = detailsMap[cat.name];
            return (
              <div key={key} className={`typ-card ${open ? 'open' : ''}`} style={{'--cat-color': cat.color}}>
                <button className="typ-card-head" onClick={() => setOpenKey(open ? null : key)}>
                  <div className="typ-card-head-left">
                    <span className="typ-card-dot" style={{background: cat.color}}/>
                    <div>
                      <div className="typ-card-name">{cat.name}</div>
                      <div className="typ-card-meta">
                        {(d?.subs?.length || cat.sub.length)} sub-categories
                      </div>
                    </div>
                  </div>
                  <span className="typ-card-chev">{open ? '–' : '+'}</span>
                </button>
                {open && (
                  <div className="typ-card-body">
                    {d?.description && <p className="typ-card-desc">{d.description}</p>}
                    <div className="typ-subs">
                      {(d?.subs || cat.sub.map(s => ({name: s, description: '', examples: []}))).map(sub => (
                        <div key={sub.name} className="typ-sub">
                          <div className="typ-sub-name">{sub.name}</div>
                          {sub.description && <p className="typ-sub-desc">{sub.description}</p>}
                          {sub.examples && sub.examples.length > 0 && (
                            <ul className="typ-sub-examples">
                              {sub.examples.map((ex, i) => <li key={i}>{ex}</li>)}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function LensesPanel() {
  return (
    <section className="typ-panel lenses-panel">
      <div className="typ-panel-inner">
        <div className="typ-panel-head">
          <div>
            <div className="static-eyebrow">Lenses</div>
            <h2 className="static-h2 static-h2-lg typ-panel-title">Additional lenses for navigating the field.</h2>
            <p className="typ-panel-blurb">
              Alongside the activity and thematic typologies, each organisation
              can be described by other dimensions: the stakeholders they
              engage, the geographic scale of their action, and their
              relationship to power and policy.
            </p>
          </div>
        </div>

        <figure className="lenses-overview">
          <img src="/assets/lenses-stakeholder-grid.png" alt="Lenses overview diagram"/>
          <figcaption>lenses at a glance</figcaption>
        </figure>

        <LensBlock
          n="01"
          icon={<svg viewBox="0 0 24 24"><circle cx="12" cy="7" r="3"/><circle cx="5" cy="16" r="2.5"/><circle cx="19" cy="16" r="2.5"/><path d="M12 10v4M12 14l-5 2M12 14l5 2"/></svg>}
          title="Stakeholders being engaged"
          blurb="Which groups does an organisation work with? "
          items={[
            "Local communities", "Social enterprises", "Activists and social movements",
            "Youth and student movements", "Cultural institutions and artists", "Elected officials",
            "Researchers / academics", "Funders / philanthropic orgs", "Small businesses",
            "Labour groups and trade unions", "Corporations", "Media / communicators",
            "Civil servants", "International orgs and networks", "Faith-based organisations",
            "Financial institutions",
          ]}
        />

        <LensBlock
          n="02"
          icon={<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 4.5 6 4.5 9S15 21 12 21M12 3c-3 3-4.5 6-4.5 9s1.5 6 4.5 9"/></svg>}
          title="Geographic scale of action"
          blurb="At what scale does an organisation work — a neighbourhood, a nation, a continent? Or a mix?"
          items={["Local", "Regional", "National", "Supranational", "International"]}
          variant="scale"
        />

        <LensBlock
          n="03"
          icon={<svg viewBox="0 0 24 24"><path d="M3 18h18M6 18v-6M12 18v-9M18 18v-3"/><path d="M3 7l6-3 6 4 6-2" strokeDasharray="2 2"/></svg>}
          title="Spectrums of change"
          blurb="Three spectrums position an organisation in relation to implementation, power, and policy. "
          variant="spectrum"
          spectrums={[
            {
              label: "How close to implementation?",
              sub: "How close to bringing things into practice?",
              from: "#FBD7E0", to: "#D6BFE6",
              steps: ["Thinking & advocating", "Indirect enabling", "Testing & piloting", "Direct enabling", "On-the-ground operator"],
            },
            {
              label: "Building vs. influencing power",
              sub: "How does an organisation see its efforts?",
              from: "#FFE99A", to: "#C9E3A3",
              steps: ["Building power from the ground up", "Amplifying grassroots voices", "Bridging movements & institutions", "Engaging decision-makers from the outside", "Operating within power structures"],
            },
            {
              label: "How close to policy?",
              sub: "How close to policy-making and policy-makers?",
              from: "#FFD6A8", to: "#F5A8A8",
              steps: ["Policy distant", "Occasional campaigner", "Policy contributor", "Policy partner", "Policy actor"],
            },
          ]}
        />
      </div>
    </section>
  );
}

export function LensBlock({ n, title, blurb, items, variant, spectrums, icon }) {
  return (
    <div className={`lens-block lens-${variant || 'default'}`}>
      <div className="lens-head">
        <div className="lens-n">{n}</div>
        {icon && <div className="lens-icon" aria-hidden="true">{icon}</div>}
        <div>
          <h3 className="lens-title">{title}</h3>
          <p className="lens-blurb">{blurb}</p>
        </div>
      </div>
      <div className="lens-body">
        {variant === 'spectrum' ? (
          <div className="spectrum-list">
            {spectrums.map((s, i) => (
              <div key={i} className="spectrum">
                <div className="spectrum-label">{s.label}</div>
                <div className="spectrum-sub">{s.sub}</div>
                <div className="spectrum-track" style={{'--spectrum-from': s.from || '#FFD97A', '--spectrum-to': s.to || '#F5A8A8'}}>
                  <div className="spectrum-axis"/>
                  {s.steps.map((st, j) => (
                    <div key={j} className="spectrum-stop" style={{left: `${(j / (s.steps.length - 1)) * 100}%`}}>
                      <div className="spectrum-node">{j+1}</div>
                      <div className="spectrum-node-label">{st}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : variant === 'scale' ? (
          <div className="scale-stack">
            {items.map((it, i) => (
              <div key={it} className="scale-row" style={{'--level': i+1, '--max': items.length}}>
                <span className="scale-dot"/>
                <span className="scale-name">{it}</span>
                <span className="scale-bar" style={{width: `${((i+1) / items.length) * 100}%`}}/>
              </div>
            ))}
          </div>
        ) : (
          <div className="lens-chip-grid">
            {items.map(it => (
              <div key={it} className="lens-chip">{it}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// ============================================================
// WheelExplorer — pan/zoom image viewer with fullscreen mode
// ============================================================

export function WheelExplorer({ src, alt }) {
  const [scale, setScale] = tpUseState(1);
  const [tx, setTx] = tpUseState(0);
  const [ty, setTy] = tpUseState(0);
  const [fs, setFs] = tpUseState(false);
  const containerRef = tpUseRef(null);
  const draggingRef = tpUseRef(null);

  const clampPan = (s, x, y) => {
    const el = containerRef.current;
    if (!el) return { x, y };
    const r = el.getBoundingClientRect();
    const maxX = (r.width * (s - 1)) / 2;
    const maxY = (r.height * (s - 1)) / 2;
    return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
  };
  const reset = () => { setScale(1); setTx(0); setTy(0); };
  const zoomIn = () => setScale(s => {
    const n = Math.min(s * 1.5, 12);
    const c = clampPan(n, tx * (n / s), ty * (n / s));
    setTx(c.x); setTy(c.y);
    return n;
  });
  const zoomOut = () => setScale(s => {
    const n = Math.max(s / 1.5, 1);
    if (n === 1) { setTx(0); setTy(0); }
    else { const c = clampPan(n, tx * (n / s), ty * (n / s)); setTx(c.x); setTy(c.y); }
    return n;
  });

  // Pointer / mouse drag panning
  const onPointerDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    draggingRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const d = draggingRef.current;
    const c = clampPan(scale, d.tx + (e.clientX - d.x), d.ty + (e.clientY - d.y));
    setTx(c.x);
    setTy(c.y);
  };
  const onPointerUp = (e) => {
    draggingRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Wheel zoom (ctrl / pinch)
  const onWheel = (e) => {
    if (!e.ctrlKey && !fs) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.002;
    setScale(s => {
      const n = Math.min(Math.max(s * (1 + delta), 1), 12);
      if (n === 1) { setTx(0); setTy(0); }
      else { const c = clampPan(n, tx * (n / s), ty * (n / s)); setTx(c.x); setTy(c.y); }
      return n;
    });
  };

  const onDblClick = (e) => {
    if (scale > 1) { reset(); return; }
    // zoom toward click point
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;
    setScale(2.5);
    setTx(-cx * 1.5);
    setTy(-cy * 1.5);
  };

  tpUseEffect(() => {
    if (!fs) return;
    const onKey = (e) => { if (e.key === 'Escape') setFs(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [fs]);

  const content = (
    <div
      ref={containerRef}
      className={`wheel-explorer ${fs ? 'wheel-explorer-fs' : ''} ${scale > 1 ? 'pannable' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      onDoubleClick={onDblClick}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
      />
      <div className="wheel-controls">
        <button onClick={(e)=>{e.stopPropagation(); zoomOut();}} aria-label="Zoom out" disabled={scale <= 1}>−</button>
        <button onClick={(e)=>{e.stopPropagation(); zoomIn();}} aria-label="Zoom in" disabled={scale >= 12}>+</button>
        <button onClick={(e)=>{e.stopPropagation(); reset();}} aria-label="Reset">⟲</button>
        <button onClick={(e)=>{e.stopPropagation(); reset(); setFs(f => !f);}} aria-label={fs ? 'Close fullscreen' : 'Expand to fullscreen'} title={fs ? 'Close' : 'Expand'}>
          {fs ? '✕' : '⤢'}
        </button>
      </div>
      <div className="wheel-hint">
        {scale > 1 ? 'Drag to pan · double-click to reset' : 'Double-click or + to zoom in'}
      </div>
    </div>
  );

  if (fs) {
    return (
      <>
        <div className="wheel-fs-backdrop" onClick={() => setFs(false)}/>
        {content}
      </>
    );
  }
  return content;
}
